import { useInfiniteQuery } from '@tanstack/react-query'
import { QueryKeys } from '../queries/querykeys'
import { BusinessAPI } from '../api/business.api'
import { UserAuth } from '../context/AuthContext'
import { LibroDiario } from '../models/LibroContable'

interface UseInfiniteLibroDiarioParams {
  businessId: number
  queryFrom: string
  queryTo: string
  limit: number
}

export const useInfiniteLibroDiario = ({
  businessId,
  queryFrom,
  queryTo,
  limit = 30
}: UseInfiniteLibroDiarioParams) => {
  const { isAuthenticated } = UserAuth()
  const businessAPI = new BusinessAPI()

  return useInfiniteQuery({
    enabled: isAuthenticated && !!businessId,
    queryKey: [
      QueryKeys.LIBRO_DIARIO,
      businessId,
      queryFrom,
      queryTo,
      limit
    ],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async ({ pageParam = 0 }) => {
      const response = await businessAPI.getDataLibroDiario({
        businessId,
        limit,
        queryFrom,
        queryTo,
        page: pageParam
      })

      const libroDiarioData = response.data

      const isPaginatedResponse = 'count' in libroDiarioData && 'libros' in libroDiarioData
      
      const libros = isPaginatedResponse
        ? (libroDiarioData as { libros: LibroDiario[], count: number }).libros
        : libroDiarioData as LibroDiario[]
      
      const count = isPaginatedResponse
        ? (libroDiarioData as { count: number }).count
        : libros.length

      if (!libros || libros.length === 0) {
        return {
          data: [],
          totalPages: 0,
          currentPage: pageParam,
          hasNextPage: false,
          count: 0
        }
      }

      const totalPages = Math.ceil(count / limit)
      
      return {
        data: libros,
        totalPages,
        currentPage: pageParam,
        hasNextPage: pageParam + 1 < totalPages,
        count
      }
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasNextPage ? lastPage.currentPage + 1 : undefined
    },
    initialPageParam: 0
  })
}
